/*
 * Foundation banners: payment failed (dunning, the panel locks when its payment grace ends), the free trial
 * countdown (payment set up or not), locked servers with "Renew now" (D20), and servers in offline grace.
 * Copy lives in `shell:banners.*`. Links point at area routes (/billing, /servers/:id).
 */
import { CreditCard, HourglassMedium, LockSimple, Warning } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useAuth } from '@/api/hooks/auth'
import { useBillingSummary } from '@/api/hooks/billing'
import { can } from '@/lib/permissions'
import { useServers } from '@/api/hooks/servers'
import { Bidi, Button, Sar } from '@/components/ui'
import { useFormat } from '@/i18n/useFormat'
import type { BannerSource } from './registry'

export const coreBanners: BannerSource[] = [
  {
    id: 'dunning',
    order: 10,
    // billing's overview lists each failed payment with its own pay button
    hiddenOn: ['/billing'],
    useBanners: () => {
      const { t } = useTranslation('shell')
      const f = useFormat()
      const { organization: org, role } = useAuth()
      if (org.billing.standing === 'ok') return []
      return [
        {
          id: 'dunning',
          tone: 'danger',
          icon: <CreditCard weight="fill" />,
          content: (
            <span>
              <strong className="font-semibold">{t('banners.dunning.title')}</strong>{' '}
              {org.billing.amountDue != null ? (
                <span className="inline-flex flex-wrap items-baseline gap-1">
                  {t('banners.dunning.amount')}{' '}
                  <Sar amount={org.billing.amountDue} className="font-semibold" />
                  {org.billing.lastPaymentFailedAt ? (
                    <Bidi
                      text={t('banners.dunning.failedOn', { date: f.date(org.billing.lastPaymentFailedAt) })}
                    />
                  ) : null}
                </span>
              ) : null}{' '}
              <span className="text-body">{t('banners.dunning.body')}</span>
            </span>
          ),
          // only roles that may pay get the button; others see the notice
          action: can(role, 'billing.manage') ? (
            <Button size="sm" variant="danger" asChild>
              <Link to="/billing">{t('banners.dunning.action')}</Link>
            </Button>
          ) : undefined,
        },
      ]
    },
  },
  {
    id: 'trial',
    order: 20,
    // the overview's trial card and billing's trial panel say the same, with more detail
    hiddenOn: ['/overview', '/billing'],
    useBanners: () => {
      const { t } = useTranslation('shell')
      const f = useFormat()
      const { organization: org, role } = useAuth()
      const summary = useBillingSummary()
      if (org.trial.status !== 'active' || !org.trial.endsAt) return []
      const days = Math.max(0, f.daysUntil(org.trial.endsAt))
      // every trial server has its payment set up in the in-portal checkout
      const card = Boolean(summary.data?.trial?.allSetUp)
      return [
        {
          id: 'trial',
          tone: card ? 'info' : days <= 2 ? 'warning' : 'info',
          icon: <HourglassMedium weight="fill" />,
          dismissible: card,
          content: (
            <span>
              <strong className="font-semibold">
                <Bidi text={t('banners.trial.title', { count: days })} />
              </strong>{' '}
              <span className="text-body">
                <Bidi
                  text={
                    card
                      ? t('banners.trial.withCard', { date: f.date(org.trial.endsAt) })
                      : t('banners.trial.noCard')
                  }
                />
              </span>
            </span>
          ),
          action: card || !can(role, 'billing.manage') ? undefined : (
            <Button size="sm" asChild>
              <Link to="/billing">{t('banners.trial.action')}</Link>
            </Button>
          ),
        },
      ]
    },
  },
  {
    id: 'locked',
    order: 15,
    // the overview's "needs your attention" list has a row per locked server
    hiddenOn: ['/overview'],
    useBanners: () => {
      const { t } = useTranslation('shell')
      const q = useServers({ state: 'locked', pageSize: 10 })
      const list = (q.data?.items ?? []).filter((s) => !s.lock?.renewedAt)
      return list.map((s) => ({
        id: `locked-${s.id}`,
        tone: 'danger' as const,
        icon: <LockSimple weight="fill" />,
        content: (
          <span>
            <strong className="font-semibold">
              <Bidi text={t('banners.locked.title', { server: s.hostname })} />
            </strong>{' '}
            <span className="text-body">{t('banners.locked.body')}</span>
          </span>
        ),
        action: (
          <Button size="sm" variant="danger" asChild>
            <Link to={`/servers/${s.id}`}>{t('banners.locked.action')}</Link>
          </Button>
        ),
      }))
    },
  },
  {
    id: 'grace',
    order: 30,
    // the overview's "needs your attention" list has a row per server in grace
    hiddenOn: ['/overview'],
    useBanners: () => {
      const { t } = useTranslation('shell')
      const f = useFormat()
      const q = useServers({ state: 'grace', pageSize: 10 })
      const list = q.data?.items ?? []
      return list.map((s) => ({
        id: `grace-${s.id}`,
        tone: 'warning' as const,
        icon: <Warning weight="fill" />,
        dismissible: true,
        content: (
          <span>
            <strong className="font-semibold">
              <Bidi text={t('banners.grace.title', { server: s.hostname })} />
            </strong>{' '}
            <span className="text-body">
              <Bidi
                text={
                  s.graceUntil
                    ? t('banners.grace.until', { date: f.date(s.graceUntil) })
                    : t('banners.grace.body')
                }
              />
            </span>
          </span>
        ),
        action: (
          <Button size="sm" variant="secondary" asChild>
            <Link to={`/servers/${s.id}`}>{t('banners.grace.action')}</Link>
          </Button>
        ),
      }))
    },
  },
]
